import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import { OTP_LENGTH, OTP_RESEND_SECONDS } from '../../constants/authPolicy';
import { isPasswordValid } from '../../utils/passwordPolicy';

const getDefaultPath = (superAdmin) =>
  superAdmin ? '/superadmin/facilities' : '/dashboard';

const resolveDefaultFacility = (userObj) => {
  const allowedFacilities = userObj?.allowedFacilities || [];
  const defaultFacilityId =
    Number(
      allowedFacilities.find((f) => f?.is_default)?.facility_id ??
      allowedFacilities[0]?.facility_id ??
      allowedFacilities[0]?.id
    ) || null;
  if (!defaultFacilityId) {
    return allowedFacilities.find((f) => f?.is_default) || allowedFacilities[0] || null;
  }
  return (
    allowedFacilities.find(
      (f) => Number(f?.facility_id ?? f?.id) === defaultFacilityId
    ) || allowedFacilities[0] || null
  );
};

const getPostLoginPath = (userObj, superAdmin) => {
  if (superAdmin) return getDefaultPath(true);
  const defaultFacility = resolveDefaultFacility(userObj);
  const needsOnboarding = defaultFacility?.status === 'setup_incomplete';
  return needsOnboarding ? '/dashboard/onboarding' : getDefaultPath(false);
};

export function useLoginPage() {
  // Phone-first unified entry (plan §12): one phone field serves login AND
  // signup. No facility selection — the server resolves facility access
  // after auth. Username/password is kept as the quiet fallback during
  // migration.
  const [method, setMethod] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimer = useRef(null);
  // Unified signup: a verified-but-unknown phone moves to a 2-field
  // registration step instead of an error.
  const [registrationToken, setRegistrationToken] = useState('');
  const [regName, setRegName] = useState('');
  const [regFacilityName, setRegFacilityName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const { login, loginWithOtp, registerOwner, logout, isAuthenticated, isSuperAdmin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      const explicit = new URLSearchParams(location.search).get('redirect') ||
                       location.state?.from?.pathname;
      if (explicit) {
        navigate(explicit, { replace: true });
        return;
      }
      navigate(getPostLoginPath(user, isSuperAdmin), { replace: true });
    }
  }, [isAuthenticated, isSuperAdmin, user, navigate, location]);

  useEffect(() => () => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
  }, []);

  const startResendCooldown = () => {
    setResendCooldown(OTP_RESEND_SECONDS);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimer.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Post-login redirect. Facility scope comes from the session itself: the
  // backend's allowedFacilities drives FacilityContext; the default is
  // stored so the rest of the app scopes immediately.
  const completeLogin = (result) => {
    const isSA = result.user?.role_name === 'Super Admin';
    const defaultFacility = resolveDefaultFacility(result.user);
    const defaultFacilityId =
      Number(defaultFacility?.facility_id ?? defaultFacility?.id) || null;

    if (!isSA && !defaultFacilityId) {
      logout();
      setError('Your account is not assigned to any facility yet. Please contact support.');
      return;
    }

    if (defaultFacilityId) {
      localStorage.setItem('active_facility_id', String(defaultFacilityId));
    }
    const explicit = new URLSearchParams(location.search).get('redirect') ||
                     location.state?.from?.pathname;

    navigate(explicit || getPostLoginPath(result.user, isSA), { replace: true });
  };

  const friendlyError = (err) => {
    const msg = err?.message || '';
    return msg.includes('fetch') || msg.includes('Network')
      ? 'Cannot reach server. Check your connection and try again.'
      : msg || 'Something went wrong. Please try again.';
  };

  const handleSendOtp = async () => {
    setError('');
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.otpSend(digits);
      if (res.success) {
        setOtpSent(true);
        setOtp('');
        startResendCooldown();
      } else {
        setError(res.message || 'Could not send the code. Try again.');
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (method === 'phone') {
      if (!otpSent) {
        await handleSendOtp();
        return;
      }
      if (otp.replace(/\D/g, '').length !== OTP_LENGTH) {
        setError(`Enter the ${OTP_LENGTH}-digit code`);
        return;
      }
    } else if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = method === 'phone'
        ? await loginWithOtp(phone.replace(/\D/g, ''), otp.replace(/\D/g, ''))
        : await login(username.trim(), password);
      if (result.success && result.isNewUser) {
        setRegistrationToken(result.registrationToken);
      } else if (result.success) {
        completeLogin(result);
      } else {
        setError(friendlyError({ message: result.error }) || (method === 'phone' ? 'Invalid or expired code' : 'Invalid credentials'));
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!regName.trim() || !regFacilityName.trim()) {
      setError('Please enter your name and your facility name');
      return;
    }
    if (!regPassword || !regConfirmPassword) {
      setError('Please enter and confirm your password');
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordValid(regPassword)) {
      setError('Password does not meet all requirements');
      return;
    }
    setLoading(true);
    try {
      const result = await registerOwner(
        registrationToken,
        regName.trim(),
        regFacilityName.trim(),
        regPassword,
        regConfirmPassword,
      );
      if (result.success) {
        completeLogin(result);
      } else {
        setError(result.error || 'Could not complete signup. Please try again.');
        // Expired token → restart phone verification.
        if (String(result.error || '').toLowerCase().includes('expired')) {
          setRegistrationToken('');
          setOtpSent(false);
          setOtp('');
        }
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMethodToggle = () => {
    setMethod((m) => (m === 'phone' ? 'password' : 'phone'));
    setError('');
    setOtp('');
    setOtpSent(false);
    setRegistrationToken('');
  };

  const handleChangePhone = () => {
    setOtpSent(false);
    setOtp('');
    setError('');
    setRegistrationToken('');
  };

  return {
    method,
    phone,
    setPhone,
    otp,
    setOtp,
    otpSent,
    resendCooldown,
    handleSendOtp,
    handleMethodToggle,
    handleChangePhone,
    isRegistering: Boolean(registrationToken),
    regName,
    setRegName,
    regFacilityName,
    setRegFacilityName,
    regPassword,
    setRegPassword,
    regConfirmPassword,
    setRegConfirmPassword,
    handleRegister,
    username,
    setUsername,
    password,
    setPassword,
    error,
    loading,
    showInquiry,
    setShowInquiry,
    handleSubmit,
  };
}
